/**
 * Workflow rail (TECH-JOB-SCREEN.md §2.1) — the left column of the tech job
 * screen. Renders the derived step list: the active step expanded with its
 * inputs inline, completed steps collapsed to a ✓ line + key fact, locked
 * steps as one muted line. Tapping an unlocked (done) step re-fires its
 * action so techs can jump back (re-record IVR, edit tests, …).
 *
 * All actions are dispatched to the page via onAction — the rail owns no
 * mutations of its own.
 */
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCircle, ChevronRight, Clock, Lock, Navigation, Phone } from "lucide-react";
import type { StepAction, WorkflowStep } from "./steps";

export interface RailCallOption {
    label: string;
    phone: string;
}

export interface WorkflowRailProps {
    steps: WorkflowStep[];
    onAction: (action: StepAction) => void;
    busy: boolean;
    /** Get There content */
    address: string | null;
    mapsUrl: string | null;
    onNavigate: () => void;
    scheduleLine: string | null;
    /** Both numbers offered when they differ (spec decision). */
    callOptions: RailCallOption[];
    /** Amber pre-scheduling / tech-notes warning, mirrored in Get There. */
    warningText: string | null;
    /** IVR content (expanded check-in / check-out steps) */
    ivrPhone: string | null;
    ivrPin: string | null;
}

function telHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

function ActionButton({
    action,
    onAction,
    busy,
}: {
    action: StepAction;
    onAction: (a: StepAction) => void;
    busy: boolean;
}) {
    const disabled = busy || (action.kind === "complete-job" && !action.enabled);
    return (
        <Button size="lg" className="h-12 w-full text-base" disabled={disabled} onClick={() => onAction(action)}>
            {action.label}
        </Button>
    );
}

export function WorkflowRail({
    steps,
    onAction,
    busy,
    address,
    mapsUrl,
    onNavigate,
    scheduleLine,
    callOptions,
    warningText,
    ivrPhone,
    ivrPin,
}: WorkflowRailProps) {
    return (
        <div className="space-y-2">
            {steps.map((step) => {
                if (step.state === "locked") {
                    return (
                        <div
                            key={step.id}
                            id={`step-${step.id}`}
                            className="flex min-h-[44px] items-center gap-3 rounded-lg border border-l-4 border-l-gray-300 bg-card px-3 py-2.5 dark:border-l-gray-700"
                        >
                            <Lock className="h-4 w-4 shrink-0 text-foreground/40" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-muted-foreground">
                                    {step.ordinal}. {step.title}
                                </div>
                                {step.detail && (
                                    <div className="truncate text-xs italic text-muted-foreground">{step.detail}</div>
                                )}
                            </div>
                        </div>
                    );
                }

                if (step.state === "done") {
                    return (
                        <button
                            key={step.id}
                            id={`step-${step.id}`}
                            type="button"
                            disabled={!step.tappable || busy}
                            onClick={step.tappable && step.action ? () => onAction(step.action!) : undefined}
                            className={cn(
                                "flex min-h-[44px] w-full items-center gap-3 rounded-lg border border-l-4 border-l-green-500 bg-card px-3 py-2.5 text-left",
                                !step.tappable && "cursor-default",
                            )}
                        >
                            <CheckCircle className="h-5 w-5 shrink-0 text-green-600" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-foreground/70">{step.title}</div>
                                {step.keyFact && (
                                    <div className="truncate text-xs text-foreground/60">{step.keyFact}</div>
                                )}
                            </div>
                            {step.tappable && <ChevronRight className="h-4 w-4 shrink-0 text-foreground/40" />}
                        </button>
                    );
                }

                // Active step — expanded with its inputs inline.
                return (
                    <div
                        key={step.id}
                        id={`step-${step.id}`}
                        className="rounded-lg border border-l-4 border-l-primary bg-card p-4 space-y-3"
                    >
                        <div className="flex items-center gap-3">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                                {step.ordinal}
                            </span>
                            <div className="text-base font-semibold">{step.title}</div>
                        </div>

                        {step.id === "get-there" && (
                            <div className="space-y-3">
                                {address && <div className="text-sm leading-snug whitespace-pre-line">{address}</div>}
                                {scheduleLine && (
                                    <div className="flex items-center gap-1.5 text-sm text-foreground/80">
                                        <Clock className="h-4 w-4 shrink-0" />
                                        {scheduleLine}
                                    </div>
                                )}
                                {warningText && (
                                    <div className="rounded-sm border-l-2 border-amber-500 bg-amber-50 py-2 pl-3 dark:bg-amber-500/10">
                                        <div className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                                            Notes for Tech
                                        </div>
                                        <div className="text-sm leading-snug whitespace-pre-line">{warningText}</div>
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                    {mapsUrl && (
                                        <Button variant="outline" size="lg" className="h-12" onClick={onNavigate}>
                                            <Navigation className="mr-2 h-4 w-4" /> Navigate
                                        </Button>
                                    )}
                                    {callOptions.map((c) => (
                                        <Button key={c.phone} asChild variant="outline" size="lg" className="h-12">
                                            <a href={telHref(c.phone)}>
                                                <Phone className="mr-2 h-4 w-4" /> {c.label}
                                            </a>
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(step.id === "ivr-checkin" || step.id === "ivr-checkout") && (
                            <div className="space-y-3">
                                {ivrPhone && (
                                    <a
                                        href={telHref(ivrPhone)}
                                        className="inline-flex min-h-[44px] items-center gap-2 text-base font-semibold text-primary"
                                    >
                                        <Phone className="h-5 w-5" /> {ivrPhone}
                                        {ivrPin ? <span className="font-mono"> · PIN {ivrPin}</span> : null}
                                    </a>
                                )}
                                <p className="text-sm text-foreground/70">
                                    Call the line, then record the confirmation number.
                                </p>
                            </div>
                        )}

                        {step.id === "do-work" && step.detail && (
                            <p className="text-sm text-foreground/70">{step.detail}</p>
                        )}

                        {step.id === "complete-wo" && step.detail && (
                            <p className="text-sm text-foreground/70">{step.detail}</p>
                        )}

                        {step.id === "wrap-up" && (
                            <p className="text-sm text-foreground/70">
                                {step.action?.kind === "complete-job" && step.action.enabled
                                    ? "All workflows submitted — wrap up and send to the office."
                                    : step.detail}
                            </p>
                        )}

                        {step.action && <ActionButton action={step.action} onAction={onAction} busy={busy} />}
                    </div>
                );
            })}
        </div>
    );
}
